/**
 * useProfileTour
 * Tour for the profile page (dual profile system)
 */

import { Tour } from '@shared/lib/tours/types'
import { TOUR_THEME } from '@shared/lib/tours/theme'
import { useCallback, useEffect } from 'react'
import { usePageReadyForTour } from './usePageReadyForTour'
import { useTrackedDriverTour } from './useTrackedDriverTour'
import { markTourAsCompleted, useTourManager } from './useTourManager'

const TOUR_NAME = 'profile'

export function useProfileTour(autoStart: boolean = false): Tour {
  const { isTourCompleted, shouldShowTour, resetTour } = useTourManager()
  const isPageReady = usePageReadyForTour()
  const startTrackedTour = useTrackedDriverTour()

  const startTour = useCallback(
    (delayMs = 0) => {
      return startTrackedTour(
        {
          ...TOUR_THEME,
          showProgress: true,
          nextBtnText: 'Siguiente',
          prevBtnText: 'Anterior',
          doneBtnText: '¡Listo!',
          progressText: '{{current}} de {{total}}',

          onDestroyed: () => {
            markTourAsCompleted(TOUR_NAME)
          },

          steps: [
            {
              element: '[data-tour="profile-tabs"]',
              popover: {
                title: 'Tus Perfiles',
                description:
                  'Gestioná tus dos perfiles: Personal (tus datos privados) y Profesional (tu perfil público para ofrecer servicios).',
                side: 'bottom',
              },
            },
            {
              element: '[data-tour="personal-profile"]',
              popover: {
                title: 'Perfil Personal',
                description:
                  'Acá están tus datos personales y de contacto. Exceptuando tu nombre y apellido, esta información es privada y solo vos la ves.',
                side: 'top',
              },
            },
            {
              element: '[data-tour="professional-tab"]',
              popover: {
                title: 'Perfil Profesional',
                description:
                  '¿Ofrecés servicios? Activá tu perfil profesional para que otros usuarios puedan encontrarte y contactarte.',
                side: 'bottom',
              },
            },
          ],
        },
        { delayMs }
      )
    },
    [startTrackedTour]
  )

  // Auto-start tour on mount if conditions are met
  useEffect(() => {
    // Only start tour when page is ready (loaded and no PWA prompt)
    if (!isPageReady) return

    if (autoStart && shouldShowTour(TOUR_NAME)) {
      return startTour(300)
    }
  }, [autoStart, isPageReady, shouldShowTour, startTour])

  const reset = () => {
    resetTour(TOUR_NAME)
  }

  return {
    startTour,
    isCompleted: isTourCompleted(TOUR_NAME),
    reset,
  }
}
